<!--
	Category tabs of countermeasure sections plus their footnotes, shared by Corridor and Curve.
	A section with `signage` lists the alignment signs above its items.
-->
<script lang="ts">
	import {
		ECountermeasureCategory,
		type TCountermeasure,
		type TSignageProvision
	} from '@safesystem/client-playbook-lib';
	import Tabs from '#lib/components/Tabs.svelte';
	import Linkified from './Linkified.svelte';
	import SignageProvision from './SignageProvision.svelte';
	import { createFootnotes } from './linkify';

	type CountermeasureSection = {
		heading: string;
		items: TCountermeasure[];
		signage?: TSignageProvision;
	};

	interface Props {
		sectionsFor: (category: ECountermeasureCategory) => CountermeasureSection[];
		/** Feature noun in the signage sentence, e.g. "curve". */
		subject: string;
		headingLevel: 3 | 4;
	}

	const { sectionsFor, subject, headingLevel }: Props = $props();

	const categories = Object.values(ECountermeasureCategory);
	let active = $state(0);

	// Linkify every item before reading the notes: footnote numbers are assigned as items are linkified, so reading them during render misses the first pass.
	const byCategory = $derived.by(() => {
		const notes = createFootnotes();
		return new Map(
			categories.map((category) => {
				const sections = sectionsFor(category).map((section) => ({
					...section,
					html: section.items.map((item) => notes.linkify(item, category))
				}));
				return [category, { sections, footnotes: notes.getFootnotes(category) }];
			})
		);
	});

	const isEmpty = (sections: CountermeasureSection[]) =>
		sections.every((section) => !section.signage && section.items.length === 0);
	const hasSignage = (signage: TSignageProvision) =>
		signage.required || signage.recommended || signage.optional;
</script>

<Tabs
	items={categories}
	bind:active
	labelFor={(category) => category}
	isDisabled={(category) => isEmpty(byCategory.get(category)?.sections ?? [])}
	label="Countermeasure category">
	{#snippet panel(category)}
		{@const { sections = [], footnotes = [] } = byCategory.get(category) ?? {}}
		<div class="output">
			{#each sections as section (section.heading)}
				{#if section.signage || section.items.length > 0}
					<svelte:element this={`h${headingLevel}`} class="sectionHeading">
						{section.heading}
					</svelte:element>
				{/if}
				{#if section.signage}
					<div class="signageProvision">
						{#if hasSignage(section.signage)}
							Provide the following Alignment Signs on the {subject} (if shown):
							<SignageProvision signageProvision={section.signage} />
						{/if}
					</div>
				{/if}
				{#if section.items.length > 0}
					<ul>
						{#each section.html as html, j (j)}
							<li><Linkified {html} /></li>
						{/each}
					</ul>
				{/if}
			{/each}
			{#if footnotes.length > 0}
				<div class="footnotes">
					<strong>Notes</strong>
					<ol>
						{#each footnotes as note, j (j)}
							<li>{note}</li>
						{/each}
					</ol>
				</div>
			{/if}
		</div>
	{/snippet}
</Tabs>

<style>
	.output {
		padding: var(--space-4) var(--space-1);

		li {
			line-height: 1.4;
		}
	}

	.sectionHeading {
		margin-top: var(--space-4);
		margin-bottom: var(--space-3);
		font-weight: var(--weight-bold);
		font-size: var(--font-size-base);
	}

	.signageProvision {
		margin: var(--space-4) var(--space-5) var(--space-4) var(--space-2);
	}

	.footnotes {
		margin: var(--space-5) var(--space-2);
		border-radius: var(--radius);
		background-color: var(--surface-alt);
		padding: var(--space-3) var(--space-4);

		ol {
			margin: var(--space-3) 0 var(--space-2);
			padding-left: var(--space-5);
		}
	}
</style>
